/* eslint-disable react/jsx-fragments */
import React from "react";
import Input from "reactstrap/lib/Input";
import CustomInput from "reactstrap/lib/CustomInput";
import get from "lodash/get";

import { gatherClasses, isFunction, isString, generateId } from "./utils";
import Icon from "../components/icon/icon";
import InputFeedback from "../components/inputFeedback/inputFeedback";
import ToggleButton from "../components/toggleButton/toggleButton";
import Tooltip from "../components/tooltip/tooltip";
import { Search, MenuClose } from "./icons";

export default class InputUtils {
  blockProp = false;

  isInvalidId = id => {
    return typeof id === "undefined" || id === null || id === "";
  };

  getCustomInputProps = props => {
    const { type, id } = props;
    if (this.isInvalidId(id)) {
      return { ...props, id: generateId(`${type}-control-`) };
    }
    return props;
  };

  renderInputComponent = props => {
    const { block, ...attr } = props;

    this.blockProp = block;

    if (
      ["checkbox", "radio", "switch", "select", "file", "range"].indexOf(
        attr.type
      ) === -1
    ) {
      return this.renderInput(attr);
    }

    const customInputProps = this.getCustomInputProps(attr);
    return this.renderCustomInput(customInputProps);
  };

  renderInput = props => {
    const { isInvalid, validationProps, disableInputFeedback, ...attr } = props;

    if (
      attr.type === "button" ||
      attr.type === "reset" ||
      attr.type === "submit"
    ) {
      return this.renderInputButton(attr);
    }

    if (attr.type === "search") {
      return this.renderInputSearch(attr);
    }

    if (attr.type === "toggle") {
      return this.renderToggleInput(props);
    }

    return (
      <React.Fragment>
        <Input
          data-testid={attr.dataTestId || `orion-${attr.type}-input`}
          {...attr}
          onChange={
            validationProps && isFunction(validationProps.handleChange)
              ? event => {
                  validationProps.handleChange(event);
                  if (isFunction(attr.onChange)) {
                    attr.onChange(event);
                  }
                }
              : attr.onChange
          }
          onBlur={
            validationProps && isFunction(validationProps.handleBlur)
              ? event => {
                  validationProps.handleBlur(event);
                  if (isFunction(attr.onBlur)) {
                    attr.onBlur(event);
                  }
                }
              : attr.onBlur
          }
          value={
            validationProps && validationProps.values
              ? get(validationProps.values, attr.name)
              : attr.value
          }
          invalid={isInvalid}
          className={gatherClasses(attr)}
        />
        {!disableInputFeedback && validationProps && (
          <InputFeedback
            show={isInvalid}
            message={get(validationProps.errors, attr.name)}
          />
        )}
      </React.Fragment>
    );
  };

  renderToggleInput = props => {
    const {
      name,
      className,
      label,
      disableInputFeedback,
      validationProps,
      isInvalid,
      disabled
    } = props;
    const isActive =
      validationProps && validationProps.values
        ? get(validationProps.values, name)
        : props.value;
    const onChange =
      validationProps && isFunction(validationProps.handleChange)
        ? validationProps.handleChange
        : props.onChange;

    return (
      <React.Fragment>
        <ToggleButton
          dataTestId={props.dataTestId}
          className={className}
          disabled={disabled}
          label={label}
          isActive={isActive}
          isInvalid={isInvalid}
          name={name}
          onClick={(fieldName, currentStatus) => {
            if (validationProps) {
              if (
                validationProps &&
                isFunction(validationProps.setFieldTouched)
              ) {
                validationProps.setFieldTouched(fieldName, true);
              }
              if (
                validationProps &&
                isFunction(validationProps.setFieldValue)
              ) {
                validationProps.setFieldValue(fieldName, currentStatus);
              }
            }
            if (isFunction(onChange)) {
              onChange.call(null, fieldName, currentStatus);
            }
          }}
        />
        {!disableInputFeedback && validationProps && (
          <InputFeedback
            show={isInvalid}
            message={get(validationProps.errors, name)}
          />
        )}
      </React.Fragment>
    );
  };

  renderInputButton = props => {
    const block = this.blockProp;
    const classes = {
      button: gatherClasses(
        props,
        "oui-btn oui-btn-outline-primary",
        block ? "oui-btn-block" : ""
      ),
      reset: gatherClasses(
        props,
        "oui-btn oui-btn-outline-primary",
        block ? "oui-btn-block" : ""
      ),
      submit: gatherClasses(
        props,
        "oui-btn oui-btn-primary",
        block ? "oui-btn-block" : ""
      )
    };

    return (
      <input
        data-testid={props.dataTestId || "orion-input-button"}
        {...props}
        className={classes[props.type]}
        value={props.label || props.type}
      />
    );
  };

  renderInputSearch = props => {
    const {
      showClearIcon,
      clearTooltipText,
      onSearch,
      onClear,
      clickable,
      dataTestId,
      ...attr
    } = props;

    function onKeyDown(e) {
      if (e.which === 13 || e.keyCode === 13) {
        if (isFunction(onSearch)) {
          onSearch(e.target.value);
        }
      } else if (e.which === 27 || e.keyCode === 27) {
        e.target.value = "";
        if (isFunction(onClear)) {
          onClear(e);
        }
      }

      if (isFunction(attr.onKeyDown)) {
        attr.onKeyDown(e);
      }
    }

    function onClearIconClick(e) {
      const el = e.target.parentElement.parentElement.querySelector(
        ".oui-search-input-textbox"
      );
      if (el && el instanceof HTMLElement) {
        el.value = "";
      }
      onClear(e);
    }

    return (
      <div
        data-testid={dataTestId || "orion-search-input"}
        className="oui-search-input-container"
      >
        <Icon src={Search} className="oui-search-input-icon" />
        <Input
          {...attr}
          type="text"
          className={gatherClasses(
            attr,
            "oui-search-input-textbox",
            showClearIcon ? "oui-input-search-enabled" : null,
            clickable ? "oui-input-clickable" : null
          )}
          onKeyDown={onKeyDown}
        />
        {showClearIcon ? (
          <span
            className="oui-search-input-clear"
            onClick={onClearIconClick}
            aria-hidden="true"
          >
            <Tooltip
              text={clearTooltipText || ""}
              showToolTip={!!isString(clearTooltipText)}
              placement="bottom"
            >
              <div className="oui-clear-icon">
                <Icon src={MenuClose} width="8px" height="8px" />
              </div>
            </Tooltip>
          </span>
        ) : null}
      </div>
    );
  };

  renderCustomInput = props => {
    const {
      isInvalid,
      options,
      validationProps,
      disableInputFeedback,
      ...attr
    } = props;
    const checkedProp = {};

    if (validationProps) {
      if (attr.type.toLowerCase() === "radio") {
        checkedProp.checked =
          get(validationProps.values, attr.name) === attr.value;
      } else if (attr.type.toLowerCase() === "checkbox") {
        const checkboxValues = get(validationProps.values, attr.name);
        if (Array.isArray(checkboxValues)) {
          checkedProp.checked = checkboxValues.includes(attr.value);
        } else {
          checkedProp.checked =
            checkboxValues === attr.value || checkboxValues === true;
        }
      }
    }

    return (
      <CustomInput
        data-testid={props.dataTestId || `orion-${attr.type}-input`}
        {...checkedProp}
        {...attr}
        onChange={
          attr.onChange
            ? attr.onChange
            : validationProps && isFunction(validationProps.handleChange)
            ? validationProps.handleChange
            : () => {}
        }
        onBlur={
          attr.onBlur
            ? attr.onBlur
            : validationProps && isFunction(validationProps.handleBlur)
            ? validationProps.handleBlur
            : () => {}
        }
        value={
          attr.value
            ? attr.value
            : validationProps && validationProps.values
            ? get(validationProps.values, attr.name)
            : ""
        }
        invalid={isInvalid}
        className={gatherClasses(
          props,
          props.type === "file" ? "oui-truncate" : null
        )}
      >
        {props.type === "select" && options && options.length > 0
          ? options.map((option, index) => {
              return (
                <option key={index} value={option.value || option.label}>
                  {option.label}
                </option>
              );
            })
          : null}
      </CustomInput>
    );
  };
}
